import { useMemo } from 'react';
import { splitQuizContent } from '@volt/shared';
import { useI18n } from '../i18n/context.js';
import { MarkdownContent } from './MarkdownContent.js';
import { QuizCard } from './QuizCard.js';

export interface AssistantContentProps {
  content: string;
  messageId: string;
  isStreaming?: boolean;
  /** The quiz in this reply can still be answered. */
  quizAnswerable?: boolean;
  onSubmitQuiz?: (text: string) => void;
}

/** An assistant answer: markdown prose, with any clarifying quiz shown as an interactive card. */
export function AssistantContent({
  content,
  messageId,
  isStreaming = false,
  quizAnswerable = false,
  onSubmitQuiz,
}: AssistantContentProps) {
  const { t } = useI18n();
  const segments = useMemo(() => splitQuizContent(content, { streaming: isStreaming }), [content, isStreaming]);

  // Almost every reply has no quiz: keep that path identical to plain markdown.
  if (segments.length === 1 && segments[0]!.type === 'text') return <MarkdownContent content={content} />;

  return (
    <>
      {segments.map((segment, index) => {
        if (segment.type === 'text') return <MarkdownContent key={index} content={segment.text} />;
        if (segment.type === 'quiz-pending') {
          return (
            <p key={index} className="quiz-pending" role="status">
              {t('quiz.preparing')}
            </p>
          );
        }
        return (
          <QuizCard
            key={index}
            quiz={segment.quiz}
            messageId={`${messageId}${index === 0 ? '' : `#${index}`}`}
            answerable={quizAnswerable}
            onSubmit={onSubmitQuiz}
          />
        );
      })}
    </>
  );
}
